/**
 * Pluralization utility
 *
 * This module wraps the `pluralize` package to provide a single, centralised
 * place for noun pluralization throughout the application.
 *
 * Import ONLY from this module — direct imports of the `pluralize` package are
 * disallowed by ESLint to keep pluralization logic easy to maintain or swap out.
 */
import pluralizeLib from "pluralize";

/**
 * Returns the plural or singular form of a noun based on the supplied count.
 *
 * @param noun  - The noun to pluralize (e.g. "day", "person", "child").
 * @param count - The numeric count that determines which form to use.
 * @param inclusive - When `true`, the returned string is prefixed with the
 *   count (e.g. `pluralize("day", 3, true)` → `"3 days"`).
 *   Defaults to `false`.
 * @returns The correctly pluralized (or singularized) noun string.
 *
 * @example
 * pluralize("day", 1)            // "day"
 * pluralize("day", 3)            // "days"
 * pluralize("person", 2)         // "people"
 * pluralize("child", 0)          // "children"
 * pluralize("item", 5, true)     // "5 items"
 */
export const pluralize = (
  noun: string,
  count: number,
  inclusive = false,
): string => pluralizeLib(noun, count, inclusive);
